/**
 * The sentence under a name: what the facts on the page add up to. Written
 * from the item's counts alone, so it can never say more than the page shows.
 */
import type { FunderItem, Headline } from '../../src/serve/items.js';
import type { WireOrganizationItem } from './api.js';
import { formatAmount, formatCount, formatPeriod, formatPeriodRange } from './format.js';

const grants = (count: number, total: number): string => `${formatCount(count)} ${count === 1 ? 'grant' : 'grants'} (${formatAmount(total)})`;

export const organizationSummary = (item: WireOrganizationItem): string => {
  // Null from the live schema, undefined from the offline server: both mean the counts are not in this build.
  const summary = item.summary ?? null;
  const received = item.grantsReceived.count;
  const given = item.grantsGiven.count;
  const span = summary === null || summary.firstPeriod === null ? null : formatPeriodRange(summary.firstPeriod, summary.lastPeriod);
  if (received === 0 && given === 0) {
    return span === null ? 'No grant to or from this organization appears in the returns the graph holds.' : `No grant to or from this organization appears in the returns the graph holds (${span}).`;
  }
  const parts: string[] = [];
  if (received > 0) {
    parts.push(`Received ${grants(received, item.grantsReceived.total)}${summary === null ? '' : ` from ${formatCount(summary.funders)} ${summary.funders === 1 ? 'funder' : 'funders'}`}`);
  }
  if (given > 0) {
    parts.push(`${received > 0 ? 'gave' : 'Gave'} ${grants(given, item.grantsGiven.total)}`);
  }
  const sentence = parts.join(' and ');
  if (summary === null) {
    return `${sentence}.`;
  }
  if (span !== null) {
    return `${sentence}, in returns for ${span}.`;
  }
  if (item.filings.length > 0) {
    // Returns are on record but none carries a period; say nothing about years rather than deny the returns.
    return `${sentence}.`;
  }
  const recorded = item.masterFile?.latestTaxPeriod ?? null;
  return `${sentence}. No e-filed return in the graph${recorded === null ? '' : `; the master file records a return for ${formatPeriod(recorded)}`}.`;
};

/**
 * The home page's one factual sentence. The grant count is every grant edge
 * the build holds, and those come from three kinds of record, so the sentence
 * names all three rather than crediting the returns alone.
 */
export const headlineSentence = (headline: Headline): string =>
  `${formatCount(headline.organizations)} organization pages, ${formatCount(headline.grants)} grants from ${formatCount(headline.funders)} funders and agencies, read from ${formatCount(headline.filers)} filers’ returns, funder websites, and federal award records.`;

export const funderSummary = (item: FunderItem): string => {
  const periods = [...item.periods].map((period) => period.period).sort();
  const span = periods.length === 0 ? null : formatPeriodRange(periods[0] ?? null, periods[periods.length - 1] ?? null);
  const placed = item.recipients.listed + item.recipients.unlisted + item.recipients.names;
  const share = placed === 0 ? null : Math.round((100 * item.recipients.listed) / placed);
  return `Reported ${formatCount(item.grantsGiven.count)} grants totaling ${formatAmount(item.grantsGiven.total)} across ${formatCount(item.periods.length)} ${item.periods.length === 1 ? 'return' : 'returns'}${span === null ? '' : ` (${span})`}${share === null ? '' : `; ${share}% went to organizations with their own page`}.`;
};
